import { ASPECT_RATIO_TOLERANCE } from '~/data/constants';
import { User } from '~/data/models/user';
import { t } from '~/utils/i18n';

export const isEmpty = (value: string): boolean => {
  return value.trim().length === 0;
};

export function isValidName(name: string): boolean {
  return name.length >= 2 && !isEmpty(name);
}

export function isValidEmail(email: string): boolean {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

export function isValidPhone(phone: string): boolean {
  const onlyNumber = /^[0-9]*$/;
  const minLength = 8;
  return onlyNumber.test(phone) && phone.length >= minLength;
}

export function validatePhone(phone: string): string[] {
  const errors = [];
  if (phone.length < 8) {
    errors.push(t('inputValidation.phoneMinLength'));
  }
  if (!/^\d+$/.test(phone)) {
    errors.push(t('inputValidation.phoneOnlyNumber'));
  }

  return errors;
}

export function isValidPassword(password: string): boolean {
  return password.length >= 6;
}

export function validatePassword(password: string): string[] {
  const errors = [];
  if (password.length < 8) {
    errors.push(t('inputValidation.passwordShort'));
  }
  if (!/\d/.test(password)) {
    errors.push(t('inputValidation.passwordMissingDigit'));
  }
  if (!/[^a-zA-Z0-9]/.test(password)) {
    errors.push(t('inputValidation.passwordMissingSpecialChar'));
  }
  return errors;
}

export function userHasText(user: User, text: string): boolean {
  return (
    user.firstName.toLowerCase().includes(text.toLocaleLowerCase()) ||
    user.lastName.toLowerCase().includes(text.toLowerCase())
  );
}

export function validateExistingUser(
  emailExists: boolean,
  phoneExists: boolean,
): string | undefined {
  if (emailExists && phoneExists) {
    return t('inputValidation.emailAndPhoneExists');
  } else if (emailExists) {
    return t('inputValidation.emailExists');
  } else if (phoneExists) {
    return t('inputValidation.phoneExists');
  }
}

export const validateImageDimensions = (
  file: Blob,
  expectedAspectRatio: number,
): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.width / img.height;
      const tolerance = ASPECT_RATIO_TOLERANCE;

      const isCorrectAspectRatio =
        Math.abs(aspectRatio - expectedAspectRatio) <= tolerance;

      resolve(isCorrectAspectRatio);
    };
    img.onerror = () => resolve(false);
    img.src = URL.createObjectURL(file);
  });
};
